import { ResettingFileUpload } from '@/components/FileUpload/ResettingFileUpload';
import { uploadDocumentsWithToast } from '@/helpers/uploadDocumentsWithToast';
import { useGetDocumentsQuery, useWatchDocumentVariants } from '@/services/documents';
import { Badge, Box, Button, TextLink, toast, useBreakpoint, useOpenClose } from '@palmetto/palmetto-components';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { UserPermissions } from 'types';

import { useUploadDocumentMutation } from '../../../services/documents';
import { isErrorWithData } from '../../../services/helpers';
import { useUpdateTaskStatusMutation } from '../../../services/tasks';
import { BaseRejectionReasons } from '../../../types/BasePackageTypes';
import {
  AllInstallPackageRejectionReasons,
  ReviewPackageActions,
  ReviewPhotoRejectionReasons,
  ReviewPhotoSectionType,
} from '../../../types/ReviewPackage';
import RequirePalmettoFinanceUser from '../../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../../auth/requirePermissions';
import { Gallery } from '../../Gallery/Gallery';
import RejectionAlert from '../../Reject/RejectionAlert';
import { RejectionModal } from '../../Reject/RejectionModal';

type InstallationPhotoItemProps = {
  section: ReviewPhotoSectionType;
  task: any;
  showUploadButton?: boolean;
};

export const InstallationPhotoItem = ({ section, task, showUploadButton = true }: InstallationPhotoItemProps) => {
  const { id, action } = useParams<{ id: any; action: ReviewPackageActions }>();
  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const { data: documents = [] } = useGetDocumentsQuery({ accountId: id, documentTypeFilter: [section.id] });
  const [uploadDocument] = useUploadDocumentMutation();
  const { isPhone } = useBreakpoint();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();

  // Track the most recent v2 upload job so its thumbnail/preview variants pop
  // in automatically when the worker finishes (no manual refresh). The LIST
  // invalidation refreshes all docs regardless of which upload it came from.
  const [variantJobId, setVariantJobId] = useState<string | undefined>(undefined);
  useWatchDocumentVariants({ accountId: id, jobId: variantJobId ?? '' }, { skip: !variantJobId });

  const rejectionReasons = useMemo(() => {
    const baseReasons = Object.keys(BaseRejectionReasons).map((baseReason) => ({
      label: BaseRejectionReasons[baseReason as keyof typeof BaseRejectionReasons],
      value: baseReason,
    }));
    const installReasons = Object.keys(ReviewPhotoRejectionReasons).map((installReason) => ({
      label: ReviewPhotoRejectionReasons[installReason as keyof typeof ReviewPhotoRejectionReasons],
      value: installReason,
    }));
    return [...baseReasons, ...installReasons];
  }, []);

  const handleFileUpload = useCallback(
    async (event: any) => {
      const files: FileList | undefined = event.target?.files;
      if (!files || !files.length) return;

      const data = new FormData();
      data.append('type', section.id);

      for (let i = 0; i < files.length; i++) {
        data.append(`files[${i}]`, files[i]);
      }

      try {
        const { jobId } = await uploadDocumentsWithToast({
          uploadDocument,
          accountId: id,
          formData: data,
          invalidateAllTags: false,
          files,
          messages: {
            success: (count) =>
              count === 1 ? `${section.title} photo uploaded` : `${count} ${section.title} photos uploaded`,
          },
        });
        if (jobId) setVariantJobId(jobId);
      } catch {
        // Upload failed — the helper already surfaced an error toast.
      }
    },
    [id, section.id, section.title, uploadDocument],
  );

  const updateAdminTask = async (value: {
    status: 'completed' | 'rejected';
    rejectionNote?: string;
    rejectionReasons?: string[];
  }) => {
    try {
      await updateTaskStatus({ accountId: id, taskId: task.id, status: value }).unwrap();
      toast.success('Task updated');
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error updating task: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error updating task');
      }
    }
  };

  const handleRejectModal = async (value: {
    rejectionReason: { value: string; label: string }[];
    rejectionNote: string;
  }) => {
    const rejectionReasons = value.rejectionReason.map((reason) => reason.value);
    await updateAdminTask({
      status: 'rejected',
      rejectionNote: value.rejectionNote,
      rejectionReasons,
    });
    closeModal();
  };

  const handleApproval = async () => {
    updateAdminTask({ status: 'completed' });
  };

  return (
    <>
      <RejectionModal
        title={`Reject ${section.title}`}
        rejectionReasons={rejectionReasons}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        onSubmit={handleRejectModal}
      />

      <Box width="100" padding="lg" borderWidth="0 0 xs 0" borderColor="separator" childGap="lg">
        <Box direction="row" width="100" justifyContent="space-between" alignItems="center" childGap="lg">
          <Box flex="auto" childGap="2xs">
            <Box direction="row" alignItems="center" childGap="xs">
              <Box as="h3" fontWeight="medium" fontSize="md">
                {section.title}
              </Box>
              <Box fontSize="sm" color="body-secondary">
                {documents.length} {documents.length === 1 ? 'file' : 'files'}
              </Box>
            </Box>
            <Box display={{ base: 'none', tablet: 'block' }} as="p" fontWeight="regular" fontSize="sm">
              {section.description}{' '}
              <TextLink href={section.documentationUrl} rel="noreferrer" target="_blank">
                See full requirements
              </TextLink>
              .
            </Box>
          </Box>

          <Box alignContent="flex-end" minWidth="25" alignItems="flex-end" childGap="xs">
            {task && task?.status !== 'ready' && (
              <Badge
                message={task.status === 'completed' ? 'Approved' : task.status}
                style={{ width: 'fit-content' }}
                variant={task.status === 'completed' ? 'success' : 'danger'}
              />
            )}
            {showUploadButton && (
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                <ResettingFileUpload
                  labelText="Upload"
                  id={`upload-${section.id}-photos`}
                  name={`${section.id}-files`}
                  size="sm"
                  onChange={handleFileUpload}
                  multiple
                  buttonText={!isPhone ? 'Upload' : null}
                  onClick={(event: any) => event.stopPropagation()}
                />
              </RequirePermissions>
            )}
          </Box>
        </Box>
        {task && task?.status === 'rejected' && (
          <RejectionAlert task={task} rejectionReasons={AllInstallPackageRejectionReasons} />
        )}
        {documents.length > 0 && <Gallery documents={documents} useLightbox />}
        {documents.length > 0 && action === ReviewPackageActions.review ? (
          <RequirePalmettoFinanceUser>
            <RequirePermissions
              permissions={[UserPermissions.admin, UserPermissions.editor]}
              checkAllPermissions={false}
            >
              <Box direction="row" justifyContent="flex-end" childGap="xs">
                {task && task?.status !== 'completed' && (
                  <Button variant="primary" size="sm" onClick={handleApproval}>
                    Approve
                  </Button>
                )}
                <Button onClick={openModal} size="sm" variant="primary" tone="danger">
                  Reject
                </Button>
              </Box>
            </RequirePermissions>
          </RequirePalmettoFinanceUser>
        ) : null}
      </Box>
    </>
  );
};
